import { useContext, createContext, useState } from 'react';

import { UserInfo, UserInfoData, UserInfoProps } from 'etc/types';
import { useUserData } from 'hooks/useUserData';
import { useReporterInfo } from 'hooks/useReporterInfo';
import { useMarketInfo } from 'context/MarketInfo/MarketInfoProvider';
import { ErrorMessage } from 'containers/ContentReporting/ContentPolicyReporting/utils/types';
import { ReportOriginType } from 'proto/generated/tintin_content_policy';

export const UserInfoContext = createContext<UserInfo>({} as UserInfo);
export const useUserInfo = () => useContext(UserInfoContext);

export const withUserInfo = <P extends UserInfoProps>(Comp: React.ComponentType<P>) => {
  return (props: Omit<P, keyof UserInfoProps>) => (
    <UserInfoContext.Consumer>{userInfo => <Comp {...(props as P)} userInfo={userInfo} />}</UserInfoContext.Consumer>
  );
};

const UserInfoProvider: React.FC = ({ children }) => {
  const [userProvidedEmail, setUserProvidedEmail] = useState<string>();
  const [refreshReporterInfo, setRefreshReporterInfo] = useState<boolean>(false);
  const [reportOriginType, setReportOriginType] = useState<ReportOriginType>(ReportOriginType.REPORT_ORIGIN_INVALID);
  const [userProvidedCountry, setUserProvidedCountry] = useState<string>();
  const { isLoading, error, userData } = useUserData();
  const {
    emailType,
    country,
    error: useReporterError,
    isLoading: reporterInfoLoading,
  } = useReporterInfo(userData?.id, userProvidedEmail, reportOriginType, refreshReporterInfo);

  const { market } = useMarketInfo();

  const data: UserInfoData = {
    username: userData?.id,
    displayName: userData?.display_name,
    email: userData?.email,
    emailType: emailType,
    country: country || userProvidedCountry,
    avatar: userData?.images?.[0] && userData.images[0].url,
    followerCount: userData?.followers ? userData.followers.total : 0,
    product: userData?.product as UserInfoData['product'],
  };

  const isRateLimited = String(useReporterError) === ErrorMessage.RATE_LIMITING_ERROR;

  return (
    <UserInfoContext.Provider
      value={{
        loading: isLoading,
        error: !!error,
        isRateLimited,
        isUserCountryError: (!!useReporterError && !isRateLimited) || (!country && !reporterInfoLoading),
        data,
        username: data.username,
        userIsLoggedIn: !!data.username,
        userCountryIsoCode: data.country || (market?.split('-').shift() as string),
        userAccountEmail: data.email,
        setUserProvidedEmail,
        setRefreshReporterInfo,
        setReportOriginType,
        refreshReporterInfo,
        setUserProvidedCountry,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export default UserInfoProvider;
